import type { Passage } from "./types";

export const passage: Passage = {
  slug: "psalm-23",
  reference: "Psalm 23",
  title: "Psalm 23 meaning: the six verses, in order",
  h1: "Psalm 23, verse by verse",
  description:
    "The whole of Bible Gateway's 2025 top six was this psalm, read in order. What each verse does, why the valley is walked through, and who is at the table.",
  targets: [
    "psalm 23 meaning",
    "the lord is my shepherd explained",
    "psalm 23 verse by verse",
  ],
  published: "2026-08-27",
  standfirst:
    "Bible Gateway's six most-read verses of 2025 were the six verses of this psalm. People are not looking up a line; they are reading the whole thing.",
  verse: {
    text: "Yahweh is my shepherd; I shall lack nothing. He makes me lie down in green pastures. He leads me beside still waters. He restores my soul.",
    ref: "Psalm 23:1–3",
  },
  context: [
    {
      text: "Even though I walk through the valley of the shadow of death, I will fear no evil, for you are with me. Your rod and your staff, they comfort me.",
      ref: "Psalm 23:4",
    },
    {
      text: "You prepare a table before me in the presence of my enemies. You anoint my head with oil. My cup runs over.",
      ref: "Psalm 23:5",
    },
    {
      text: "Surely goodness and loving kindness shall follow me all the days of my life, and I will dwell in Yahweh's house forever.",
      ref: "Psalm 23:6",
    },
  ],
  takeaways: [
    "It is six verses and it moves: pasture, then valley, then a table set in front of enemies, then a house. The order is the argument.",
    "Verse 4 says through the valley, not around it — the psalm never claims the dark place is avoided, only that it is accompanied.",
    "The table in verse 5 is set in the presence of enemies who are still present; the meal happens anyway, which is a stranger and better image than the one on the funeral card.",
    "The shepherd language is not sentimental in its own setting: a shepherd's rod was a weapon, and the comfort in verse 4 comes from a stick.",
  ],
  body: [
    {
      type: "h2",
      id: "why-the-whole-psalm",
      text: "Why the whole psalm and not one line",
    },
    {
      type: "p",
      text: "In 2025 the six most-read verses on Bible Gateway were Psalm 23:4, 23:1, 23:6, 23:5, 23:3 and 23:2 — the entire psalm, out of order in the ranking but plainly read as a unit. That is unusual. Nearly every other popular text circulates as a single line, and this one is being read from beginning to end by enough people to take the top six places on a site with billions of page views.",
    },
    {
      type: "p",
      text: "It is worth taking that seriously as a fact about the psalm rather than about its fame, because the psalm rewards it. Read as six separate verses it is a collection of pleasant images. Read in order it is a journey with a shape, and the shape is what people are actually coming back for.",
    },
    {
      type: "h2",
      id: "the-shape",
      text: "The shape of it",
    },
    {
      type: "ol",
      items: [
        "Verses 1–3: provision. Pasture, water, rest — and note the verb in verse 2, he MAKES me lie down. Sheep do not lie down while anxious; the conditions are arranged until rest becomes possible.",
        "Verse 4: the valley. The pronouns change here from he to you — the psalm stops describing God and starts speaking to him, exactly at the point where it gets dark.",
        "Verse 5: the table. A meal, in the open, with enemies watching. Provision that is not private and not deferred until the danger has gone.",
        "Verse 6: the house. Goodness and mercy following rather than leading, and a destination rather than a circuit.",
      ],
    },
    {
      type: "p",
      text: "That pronoun shift in verse 4 is the one detail most worth carrying away. For three verses the psalm talks about a shepherd in the third person. In the valley it turns and addresses him directly. Whatever else this psalm knows about trouble, it knows that trouble is where description stops and address begins.",
    },
    {
      type: "h2",
      id: "the-rod-and-the-staff",
      text: "The rod and the staff",
    },
    {
      type: "p",
      text: "Two tools, and only one of them is the crook of the illustrations. The rod was a club — a short, heavy stick carried against predators and thieves. The comfort in verse 4 is not the comfort of being soothed; it is the comfort of walking with somebody who is armed. This is a psalm attributed to a man remembered for killing a lion and a bear before he was a soldier, and the image is not decorative.",
    },
  ],
  misreading: {
    claim:
      "That it is a funeral psalm — a gentle text about dying, and a promise of an untroubled life for those who trust enough.",
    correction: [
      "It is read at funerals for good reasons and it is not about death. Verse 4 says the valley of the shadow of death, which in Hebrew is a phrase about deep darkness rather than a description of dying, and the sentence is about walking through it and coming out. The psalm's own destination in verse 6 is a house lived in, not a grave.",
      "The second half of the misreading is more damaging. Nothing here promises the absence of trouble: there is a valley, there are enemies, and both are present in the text at the same time as the provision. Verse 5 is remarkable precisely because the enemies do not leave — the table is set in front of them. A reading that turns this into a guarantee of a smooth life has removed the two features that make it usable when life is not smooth.",
      "Read as it stands, the psalm claims something more specific and more testable: that the dark place is accompanied, that provision arrives inside the situation rather than after it, and that the direction of travel is toward a house. Those are three claims a person can actually hold on to on a Tuesday.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Say it out loud, slowly, and stop at the first line that catches rather than finishing. This is the psalm most often recited fluently and thought about least — familiarity is the enemy here in the same way it is with a well-remembered story, and the cure is speed rather than effort: go slower and the sentences stop sliding past.",
    },
    {
      type: "p",
      text: "It is also the best single passage to learn by heart, because it is long enough to hold a whole situation rather than a single mood, and because six verses is genuinely achievable over a fortnight. {The-method-that-actually-keeps-a-passage} is ninety seconds of review a day rather than one heroic evening.",
      links: {
        "The-method-that-actually-keeps-a-passage":
          "/blog/how-to-memorize-bible-verses",
      },
    },
    {
      type: "note",
      text: "If you only ever do one thing with this psalm, notice the pronoun change in verse 4. It is the whole psalm in one grammatical move.",
    },
  ],
  faq: [
    {
      q: "What does 'the valley of the shadow of death' actually mean?",
      a: "The Hebrew phrase carries the sense of deep darkness or gloom rather than dying specifically, which is why translations vary between the traditional rendering and 'the darkest valley'. Either way the sentence is about passing through it, and the psalm ends in a house rather than a tomb.",
    },
    {
      q: "Why is Psalm 23 so popular?",
      a: "It was the whole of Bible Gateway's top six most-read verses in 2025. The most likely reason is the one visible in the text: it holds provision and danger in the same six verses without resolving one into the other, which is a rare thing to find in something short enough to read in thirty seconds.",
    },
    {
      q: "Is Psalm 23 a funeral psalm?",
      a: "It is read at funerals and it is not about death. It is about being accompanied through deep darkness and arriving somewhere — which is why it works at a funeral, and why it also works on an ordinary bad Tuesday.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain. It prints the divine name as Yahweh where the familiar renderings have 'The LORD'.",
    },
  ],
  related: [
    "how-to-memorize-bible-verses",
    "morning-quiet-time-routine",
    "bible-in-a-year-on-your-phone",
  ],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-rest",
  ],
  relatedPassages: ["psalm-91", "isaiah-41-10"],
};
